import { ReactComponent as Activate } from '../../assets/images/activate.svg';
import { ReactComponent as Cancel } from '../../assets/images/cancel.svg';
import { ReactComponent as Deactivate } from '../../assets/images/deactivate.svg';
import { ReactComponent as Download } from '../../assets/images/download.svg';
import { ReactComponent as Duplicate } from '../../assets/images/duplicate.svg';
import { ReactComponent as Edit } from '../../assets/images/edit.svg';
import { ReactComponent as KervIndicator } from '../../assets/images/lineItemKervIndicator.svg';
import { ReactComponent as Link } from '../../assets/images/link.svg';
import { ReactComponent as Pause } from '../../assets/images/pause.svg';
import { ReactComponent as Performance } from '../../assets/images/performance.svg';
import { ReactComponent as Remove } from '../../assets/images/remove.svg';
import { ReactComponent as Resume } from '../../assets/images/resume.svg';
import { ReactComponent as Share } from '../../assets/images/share.svg';
import { ReactComponent as Suspend } from '../../assets/images/suspend.svg';

type ActionType =
  | 'GoByLink'
  | 'Edit'
  | 'Duplicate'
  | 'Remove'
  | 'Cancel'
  | 'Pause'
  | 'Resume'
  | 'Suspend'
  | 'Download'
  | 'Performance'
  | 'Activate'
  | 'Deactivate'
  | 'GhostMode'
  | 'Download_All'
  | 'DownloadTotalImpressions'
  | 'DownloadLocations'
  | 'DownloadAudiences'
  | 'DownloadPlatforms'
  | 'DownloadGenres'
  | 'KervIndicator';

export type IconComponents = {
  [key in ActionType]: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

export type IconActionType = {
  name: string;
  tooltip: string;
};

export const getActionIcon = (name: string, isCampaign: boolean, hasPermission?: boolean): IconActionType => {
  const actionTarget = isCampaign ? 'Campaign' : 'Line Item';
  const resumeDisabledTooltip = isCampaign
    ? 'Unable to resume campaign without an approved Ad for a line item'
    : 'Unable to resume line item without an approved Ad';
  const permissionTooltip = hasPermission === false ? `You are not permitted to ${name} ${actionTarget}` : '';

  const actions: Record<string, IconActionType> = {
    edit: { name: 'Edit', tooltip: `Edit ${actionTarget}` },
    duplicate: { name: 'Duplicate', tooltip: `Duplicate ${actionTarget}` },
    pause: { name: 'Pause', tooltip: `Pause ${actionTarget}` },
    cancel: { name: 'Cancel', tooltip: permissionTooltip || `Cancel ${actionTarget}` },
    resume: { name: 'Resume', tooltip: permissionTooltip || `Resume ${actionTarget}` },
    remove: { name: 'Remove', tooltip: `Delete ${actionTarget}` },
    suspend: { name: 'Suspend', tooltip: permissionTooltip || `Suspend ${actionTarget}` },
    download: { name: 'Download', tooltip: 'Download Report' },
    disabledResume: { name: 'Resume', tooltip: resumeDisabledTooltip },
  };

  return actions[name];
};

type Permissions = {
  updateReport?: boolean;
  deleteReport?: boolean;
  readLineItems?: boolean;
  getCampaign?: boolean;
};

export const getReportActionIcon = (
  name: string,
  permissions: Permissions = { updateReport: true, deleteReport: true, readLineItems: true, getCampaign: true }
): IconActionType => {
  const actionTarget = 'Report';

  const actions: Record<string, IconActionType> = {
    download: { name: 'Download', tooltip: `Download ${actionTarget}` },
    performance: {
      name: 'Performance',
      tooltip:
        permissions.readLineItems && permissions.getCampaign
          ? `View Performance`
          : 'You are not permitted to view performance',
    },
    remove: {
      name: 'Remove',
      tooltip: permissions.deleteReport ? `Delete ${actionTarget}` : 'You are not permitted to remove report',
    },
    pause: {
      name: 'Pause',
      tooltip: permissions.updateReport ? `Stop sending ${actionTarget}` : 'You are not permitted to pause report',
    },
    resume: {
      name: 'Resume',
      tooltip: permissions.updateReport ? `Resume sending ${actionTarget}` : 'You are not permitted to resume report',
    },
  };
  return actions[name];
};

export const ICON_COMPONENTS: IconComponents = {
  Edit: Edit,
  Duplicate: Duplicate,
  Remove: Remove,
  Cancel: Cancel,
  Pause: Pause,
  Resume: Resume,
  Suspend: Suspend,
  Download: Download,
  Download_All: Download,
  Performance: Performance,
  GoByLink: Link,
  Activate: Activate,
  Deactivate: Deactivate,
  GhostMode: Share,
  DownloadTotalImpressions: Download,
  DownloadLocations: Download,
  DownloadAudiences: Download,
  DownloadPlatforms: Download,
  DownloadGenres: Download,
  KervIndicator: KervIndicator,
};
